import React, { useEffect, useState, useCallback } from 'react';
import { Globe, Search, MapPin, Monitor, X, Calendar, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Pagination } from '../components/Pagination';
import { settingsAPI } from '../lib/api';
import { toast } from 'sonner';

export const AdminVisitors = () => {
    const [visitors, setVisitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [backfilling, setBackfilling] = useState(false);
    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalVisitors, setTotalVisitors] = useState(0);
    const ITEMS_PER_PAGE = 50;

    const loadVisitors = useCallback(async () => {
        try {
            const skip = (currentPage - 1) * ITEMS_PER_PAGE;
            const params = { skip, limit: ITEMS_PER_PAGE };
            if (searchQuery) params.search = searchQuery;
            if (dateFilter) params.date = dateFilter;
            const res = await settingsAPI.getVisitors(params);
            setVisitors(res.data.visitors || []);
            setTotalVisitors(res.data.total || 0);
        } catch (err) {
            console.error('Error loading visitors:', err);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery, dateFilter]);

    useEffect(() => { loadVisitors(); }, [loadVisitors]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(search);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const totalPages = Math.ceil(totalVisitors / ITEMS_PER_PAGE);

    const formatVisitDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleString('fr-FR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return dateStr;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="admin-visitors-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <Globe className="w-6 h-6 text-blue-700" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">
                                Visiteurs du site
                            </h1>
                            <p className="text-muted-foreground">{totalVisitors} enregistrement{totalVisitors !== 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    <Button
                        onClick={async () => {
                            setBackfilling(true);
                            try {
                                const res = await settingsAPI.backfillVisitorGeo();
                                toast.success(res.data.message);
                                loadVisitors();
                            } catch {
                                toast.error('Erreur lors de la mise a jour');
                            } finally {
                                setBackfilling(false);
                            }
                        }}
                        variant="outline"
                        disabled={backfilling}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50 rounded-xl"
                        data-testid="backfill-geo-btn"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${backfilling ? 'animate-spin' : ''}`} />
                        {backfilling ? 'Mise a jour...' : 'Actualiser localisations'}
                    </Button>
                    <Button
                        onClick={async () => {
                            if (!window.confirm('Êtes-vous sûr de vouloir effacer tout l\'historique des visiteurs ?\nCette action est irréversible. Sauvegardez la BDD avant.')) return;
                            try {
                                await settingsAPI.clearVisitorHistory();
                                toast.success('Historique des visiteurs effacé');
                                loadVisitors();
                            } catch {
                                toast.error('Erreur lors de la suppression');
                            }
                        }}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                        data-testid="clear-visitor-history-btn"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Effacer historique
                    </Button>
                </div>

                {/* Filters */}
                <Card className="mb-6 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1 flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher par ville, pays, IP, FAI..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-10 bg-white/80 border-jungle/20 rounded-xl"
                                        data-testid="visitors-search-input"
                                    />
                                </div>
                                {search && (
                                    <Button variant="ghost" onClick={() => setSearch('')} className="text-destructive hover:bg-destructive/10 rounded-xl">
                                        <X className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <input
                                    type="date"
                                    value={dateFilter}
                                    onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                                    className="px-3 py-2 text-sm border rounded-xl bg-white/80 border-jungle/20 focus:ring-2 focus:ring-leaf/30"
                                    data-testid="visitors-date-filter"
                                />
                                {dateFilter && (
                                    <Button variant="ghost" size="sm" onClick={() => setDateFilter('')} className="text-destructive hover:bg-destructive/10 rounded-xl">
                                        <X className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Visitors Table */}
                <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader>
                        <CardTitle className="font-heading text-jungle">
                            {totalVisitors} visiteur{totalVisitors !== 1 ? 's' : ''}
                            {searchQuery && <span className="text-sm font-normal text-muted-foreground ml-2">pour "{searchQuery}"</span>}
                            {dateFilter && <span className="text-sm font-normal text-muted-foreground ml-2">le {dateFilter}</span>}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {visitors.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-jungle/10">
                                            <TableHead className="text-jungle/70">Date / Heure</TableHead>
                                            <TableHead className="text-jungle/70">Adresse IP</TableHead>
                                            <TableHead className="text-jungle/70">Localisation</TableHead>
                                            <TableHead className="text-jungle/70 hidden md:table-cell">FAI</TableHead>
                                            <TableHead className="text-jungle/70 text-center">Visites</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {visitors.map((v, idx) => (
                                            <TableRow key={`${v.ip}-${v.date}-${idx}`} className="border-jungle/10" data-testid={`visitor-row-${idx}`}>
                                                <TableCell>
                                                    <span className="text-sm text-muted-foreground">{formatVisitDate(v.first_visit)}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="font-mono text-xs text-jungle">{v.ip}</span>
                                                </TableCell>
                                                <TableCell>
                                                    {v.city || v.country ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <MapPin className="w-3.5 h-3.5 text-leaf flex-shrink-0" />
                                                            <div>
                                                                <p className="text-sm font-medium text-jungle">
                                                                    {[v.city, v.region].filter(Boolean).join(', ')}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">{v.country}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground italic">Localisation inconnue</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <span className="text-xs text-muted-foreground">{v.isp || '-'}</span>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    <Badge variant="outline" className="rounded-full border-jungle/20">{v.visits}</Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <Globe className="w-12 h-12 mx-auto mb-4 text-jungle/20" />
                                <p className="text-muted-foreground">Aucun visiteur trouve</p>
                            </div>
                        )}
                    </CardContent>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalVisitors}
                        setCurrentPage={setCurrentPage}
                        label="visiteurs"
                    />
                </Card>
            </div>
        </div>
    );
};
